/**
 * Piglet registration overview shown at the top of the farm dashboard.
 *
 * Mirrors `GET /dashboard/piglets` (swagger:
 * `HealthSystem.API.Controllers.Dashboards.Responses.PigletRegistrationDashboardResponse`). Hand-written
 * rather than taken from the generated client because regenerating `HealthSystemApi.ts` pulls in unrelated
 * backend changes - see `PigletRegistrationService`. Field names match the API exactly so the two stay easy
 * to diff.
 *
 * Conventions from the API: every `*Percent` is a whole percentage (0-100), never a 0-1 fraction, and the
 * three weight-class percentages are shares of `classifiedCount`, not of `registered`.
 *
 * Expected volume is per production cycle, not per week: a farm on a 2-week batch system may register 200
 * piglets in one week and 800 in the next and still be on target. So the judgement - the totals against
 * expected, and the red flag - is made per completed cycle, never per week. A farm without a production
 * schedule is sent as a 1-week cycle, which reads exactly like a weekly view.
 */
export interface PigletRegistrationDashboard {
  /** False when the farm turned the widget off. Everything else is then zero and `weeks` is empty. */
  isEnabled: boolean;
  /** Number of weeks the period covers. */
  weeksCount: number;
  /** First day of the first cycle shown. Earlier than the period's own start when it begins mid-cycle. */
  fromDate: string;
  /** Piglets registered across the whole period, the cycle in progress included. */
  totalRegistered: number;
  /** Piglets registered in the completed cycles - the figure `totalExpected` is compared with. */
  registeredInCompletedCycles: number;
  /** Piglets expected across the completed cycles. The cycle in progress is not in it. */
  totalExpected: number;
  /** Piglets the farm is expected to register in one production cycle. */
  expectedPerCycle: number;
  /** Length in weeks of the farm's current production cycle; 1 without a schedule. */
  cycleWeeks: number;
  /** False when the farm has no production schedule and every week is treated as its own cycle. */
  hasProductionSchedule: boolean;
  /** Registered piglets with a known weight category - the denominator of the three percentages. */
  classifiedCount: number;
  lightPercent: number;
  mediumPercent: number;
  heavyPercent: number;
  /**
   * Mean birth weight in grams across the period. Not purely a measurement: a piglet registered without a
   * weight gets one derived from its weight category, so the figure is only as precise as the farm's
   * weighing habit. `weighedCount` is the denominator and the honest measure of that.
   */
  averageBirthWeightGrams: number;
  /** Registered piglets that have a birth weight stored. */
  weighedCount: number;
  /** `totalExpected - registeredInCompletedCycles`, already floored at 0 by the API. Completed cycles only. */
  missingVsExpected: number;
  /** `registeredInCompletedCycles` as a percentage of `totalExpected`. Can exceed 100. */
  registeredPercentOfExpected: number;
  /**
   * Weeks that had at least one registration, oldest first. Weeks without registrations are omitted
   * rather than reported as zero. Carries the per-week detail - weight classes, mortality - that the
   * cycles do not.
   */
  weeks: PigletRegistrationWeek[];
  /** Production cycles covering the period, oldest first. The last one is usually still in progress. */
  cycles: PigletRegistrationCycle[];
}

export interface PigletRegistrationWeek {
  year: number;
  weekNumber: number;
  /** First day of the week. Carried alongside the number because week numbers repeat across a year boundary. */
  weekStartDate: string;
  /** Piglets born in this week. */
  registered: number;
  /** Piglet deaths recorded in this week. */
  deadCount: number;
  /** `deadCount` as a percentage of `registered`. */
  mortalityPercent: number;
  /** Piglets of this week with a known weight category. */
  classifiedCount: number;
  lightPercent: number;
  mediumPercent: number;
  heavyPercent: number;
  /** Mean birth weight in grams for this week. See the dashboard-level field for the caveat. */
  averageBirthWeightGrams: number;
  /** Piglets of this week that have a birth weight stored. */
  weighedCount: number;
}

/** One production cycle: the unit expected volume is set for and judged in. */
export interface PigletRegistrationCycle {
  startDate: string;
  /** Exclusive: the first day after the cycle. */
  endDate: string;
  /**
   * The schedule's cycle length. `weeks` can hold fewer when a schedule change cut the cycle short; its
   * `expected` is then already prorated.
   */
  cycleWeeks: number;
  /** Piglets born in the cycle so far. */
  registered: number;
  expected: number;
  /** `registered` as a whole percentage of `expected`. */
  percentOfExpected: number;
  /** False for the cycle in progress, which is shown but never judged. */
  isComplete: boolean;
  /** Every week of the cycle, the ones that registered nothing and the ones still to come included. */
  weeks: PigletRegistrationCycleWeek[];
}

export interface PigletRegistrationCycleWeek {
  weekStartDate: string;
  year: number;
  weekNumber: number;
  registered: number;
  /** A week of the cycle in progress that has not started yet. */
  isFuture: boolean;
}

/** Period options offered by the dashboard's period selector. */
export const PIGLET_PERIOD_OPTIONS = [4, 8, 12, 26, 52] as const;

export const DEFAULT_PIGLET_PERIOD_WEEKS = 12;

/**
 * A completed cycle is flagged when it lands under this share of its own expected volume. The cycle in
 * progress never is: half a cycle in, half the expected volume is exactly on target.
 */
export const BELOW_EXPECTED_THRESHOLD = 0.8;

export const isCycleBelowExpected = (
  cycle: Pick<PigletRegistrationCycle, 'isComplete' | 'expected' | 'registered'>
): boolean => cycle.isComplete && cycle.expected > 0 && cycle.registered < cycle.expected * BELOW_EXPECTED_THRESHOLD;

/** The first and last week number of a cycle, for labels like "40–41". Equal for a 1-week cycle. */
export const cycleWeekSpan = (cycle: PigletRegistrationCycle): { first: number; last: number } => ({
  first: cycle.weeks[0]?.weekNumber ?? 0,
  last: cycle.weeks[cycle.weeks.length - 1]?.weekNumber ?? 0
});

/** The per-week detail of `weeks`, rolled up over the weeks of one cycle. */
export interface PigletRegistrationCycleDetail {
  deadCount: number;
  mortalityPercent: number;
  averageBirthWeightGrams: number;
  weighedCount: number;
  classifiedCount: number;
  lightPercent: number;
  mediumPercent: number;
  heavyPercent: number;
}

/**
 * Rolls the matching entries of `weeks` up to one cycle, or null when none of its weeks registered
 * anything. Matched on year and week number rather than on the start date string, which the two lists are
 * not promised to format identically.
 *
 * Averages are weighted by their own denominators - birth weight by `weighedCount`, the weight classes by
 * `classifiedCount` - so a busy week counts for more than a quiet one, as it does in the period totals.
 */
export const summariseCycle = (
  cycle: PigletRegistrationCycle,
  weeks: PigletRegistrationWeek[]
): PigletRegistrationCycleDetail | null => {
  const inCycle = weeks.filter((week) =>
    cycle.weeks.some((cycleWeek) => cycleWeek.year === week.year && cycleWeek.weekNumber === week.weekNumber)
  );

  if (inCycle.length === 0) return null;

  const sum = (pick: (week: PigletRegistrationWeek) => number) =>
    inCycle.reduce((total, week) => total + pick(week), 0);
  const registered = sum((week) => week.registered);
  const deadCount = sum((week) => week.deadCount);
  const weighedCount = sum((week) => week.weighedCount);
  const classifiedCount = sum((week) => week.classifiedCount);
  const share = (pick: (week: PigletRegistrationWeek) => number) =>
    classifiedCount > 0 ? sum((week) => pick(week) * week.classifiedCount) / classifiedCount : 0;

  return {
    deadCount,
    mortalityPercent: registered > 0 ? (deadCount / registered) * 100 : 0,
    averageBirthWeightGrams:
      weighedCount > 0 ? Math.round(sum((week) => week.averageBirthWeightGrams * week.weighedCount) / weighedCount) : 0,
    weighedCount,
    classifiedCount,
    lightPercent: share((week) => week.lightPercent),
    mediumPercent: share((week) => week.mediumPercent),
    heavyPercent: share((week) => week.heavyPercent)
  };
};

/**
 * Period mortality, the one figure the API has no field for. `missingVsExpected` and
 * `registeredPercentOfExpected` used to be derived here too; both are now sent, so take them from the
 * response rather than recomputing - the API's own rounding is the one the backend reports elsewhere.
 */
export interface PigletRegistrationTotals {
  /** Piglet deaths across the period, summed from the weeks. */
  deadTotal: number;
  /** `deadTotal` as a share of `totalRegistered`. */
  mortalityPercent: number;
}

export const getPigletRegistrationTotals = (dashboard: PigletRegistrationDashboard): PigletRegistrationTotals => {
  const deadTotal = dashboard.weeks.reduce((sum, week) => sum + week.deadCount, 0);

  return {
    deadTotal,
    mortalityPercent: dashboard.totalRegistered > 0 ? (deadTotal / dashboard.totalRegistered) * 100 : 0
  };
};
